import { Component, Inject, NgZone, OnInit, ViewChild, Directive } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTable as MatTable, MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { interval, merge, of, Subject } from 'rxjs';
import { catchError, debounce, map } from 'rxjs/operators';
import { updateStickyColumnStyles } from '../../../../shared/table';
import { getQuery } from '../../../../shared/utils';
import { IPartner } from '../../../models/partner';
import { IPartnerRequest } from '../../../models/partnerrequest';
import { EnumService } from '../../../service/enum.service';
import { PartnerService } from '../../../service/partner.service';
import { PopupService } from '../../../service/popup.service';

@Directive()
abstract class PartnerListDialogComponent implements OnInit {
  formGroup: UntypedFormGroup;

  displayedColumns: string[] = ['Select', 'Anrede', 'Vorname', 'Nachname', 'Strasse', 'Plz', 'Ort', 'Geburtsdatum', 'Email', 'Telefonnummer'];
  dataSource = new MatTableDataSource<IPartner>();

  partner = new Subject<IPartner[]>();

  selectedPartner: IPartner;

  showEinzelunternehmen: boolean;

  validateAlter: (alter: Date) => boolean;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatTable, { static: true }) table: MatTable<IPartner>;

  constructor(public popupService: PopupService, public enumService: EnumService,
    private ngZone: NgZone, private fb: UntypedFormBuilder, public dialogRef: MatDialogRef<any>, @Inject(MAT_DIALOG_DATA) public data: any,
    public translateService: TranslateService) {
    this.partner.subscribe(data => {
      this.dataSource.data = data;
      this.selectedPartner = null;
      this.select.setValue(null, { emitEvent: false });
      updateStickyColumnStyles(this.table, this.ngZone);
    });
    this.showEinzelunternehmen = this.data.showEinzelunternehmen;
    this.validateAlter = this.data.validateAlter;
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      select: new UntypedFormControl(''),
      name: new UntypedFormControl('', Validators.required)
    });
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'Anrede': return this.enumService.getAnrede(item.Anrede);
        case 'Nachname': return item.Privatperson ? item.Nachname : item.Firmenname;
        case 'Strasse': return item.Adresse.Strasse;
        case 'Plz': return item.Adresse.Plz;
        case 'Ort': return item.Adresse.Ort;
        default: return item[property];
      }
    };
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    merge(
      this.sort.sortChange,
      this.paginator.page
    ).subscribe(() => {
      updateStickyColumnStyles(this.table, this.ngZone);
    });
    this.onValueChanged();
  }

  get select() { return this.formGroup.get('select'); }
  get name() { return this.formGroup.get('name'); }

  abstract loadPartner(): void;

  onValueChanged(): void {
    this.select.valueChanges.subscribe(() => {
      this.selectedPartner = this.select.value;
      if (this.validateAlter(this.selectedPartner.Geburtsdatum)) {
        this.dialogRef.close(this.selectedPartner.PartnerId);
      }
    });
    this.name.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        if (this.name.valid) {
          this.loadPartner();
        }
      });
  }

  close() {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'app-partner-list-dialog',
  templateUrl: './partner-list-dialog.component.html',
  styleUrls: ['./partner-list-dialog.component.scss']
})
export class VersicherungsnehmerListDialogComponent extends PartnerListDialogComponent {

  constructor(private route: ActivatedRoute, private partnerService: PartnerService, popupService: PopupService, enumService: EnumService,
    ngZone: NgZone, fb: UntypedFormBuilder, dialogRef: MatDialogRef<VersicherungsnehmerListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: any, translateService: TranslateService) {

    super(popupService, enumService, ngZone, fb, dialogRef, data, translateService);
  }

  loadPartner() {
    this.partnerService.getVersicherungsnehmerBySearchParameter({
      Name: this.name.value,
      Einzelunternehmen: this.showEinzelunternehmen
    } as IPartnerRequest, getQuery()).pipe(
      map(data => data.Partner), catchError(() => of([]))
    )
      .subscribe(data => {
        this.partner.next(data);
      });
  }
}
